import {
  authSchema,
  availabilitySchema,
  basePlanSchema,
  baseReportSchema,
  compactDutyReportSchema,
  dayRosterSchema,
  leaveOrArrangementReportSchema,
  preferenceSchema,
  referenceDateSchema,
  requestReportSchema
} from './CollectedReports/schema';

export type Param = {
  name: string;
  label: string;
  fieldType: string;
  required: boolean;
};

export type BaseReport = {
  name: REPORT_TYPES;
  label: string;
  preferredOutputFormat: string;
  parameters: Param[];
};

export type Participant = {
  entityId: number;
  name: string;
  participantEntityId: string;
  role: string;
  validFrom: Date;
  validTo: Date;
};

export type Project = {
  entityId: number;
  name: string;
  description: string;
  isFavorite: boolean;
  validFrom: Date;
  validTo: Date;
  participants: Participant[];
};

export enum BASE_REPORTS {
  'WEEK_REPORT' = 'week_report',
  'DUTY_REPORT_COMP' = 'duty_report_compact',
  'DAY_ROSTER' = 'D18_0_000',
  'REQUEST_REPORT' = 'request_report',
  'AVAILABILITY' = 'availability_report',
  'HOURMANAGEMENT_REPORT' = 'hourmanagement_report',
  'LEAVE_REQUEST' = 'leave_request',
  'DUTY_CODE_REPORT' = 'duty_code_report',
  'PREFERENCES' = 'preference_excel_json',
  'PROJECTPLANNING_REPORT' = 'projectplanning_report',
  'ORGANIZATIONAL_UNIT_DISTRIBUTION' = 'organizational_unit_distribution'
}

export enum ADVANCED_REPORTS {
  'BASE_PLAN' = 'base_plan',
  'DUTY_OVERLAP' = 'duty_overlap',
  'ENDING_BENEFITS' = 'ending_benefits',
  'AUTHORIZATION_ADV_REPORT' = 'authorisation_advanced_report',
  'TRAVEL_REPORT' = 'travel_report',
  'HOURS_PER_PLANUNIT' = 'hours_per_planunit',
  'PUBLISH_REPORT' = 'publish_report',
  'NO_TRAVEL_ARRANGEMENT_REPORT' = 'no_travel_arrangement_report',
  'MISSING_FLOW' = 'missing_flow',
  'CAPPLAN' = 'capplan',
  'BENEFITS' = 'benefits_excel',
  'ARRANGEMENT_REPORT' = 'arrangements_report',
  'DUTY_LINE_EXT' = 'duty_report',
  'REQUEST_RESPONSE' = 'request_response'
}

export enum ADMIN_REPORTS {
  'YEAR_CLOSURE' = 'year_closure_excel',
  'FLEX_TRAVEL_ARRANGEMENT' = 'flex_travel_arrangement_validity',
  'EMPLOYEE_TRAVEL_MEANS' = 'employee_travel_means',
  'FUNCTION_REPORT' = 'function_report',
  'BALANCE_BUDGET_OVERVIEW' = 'balanceBudgetOverviewReport',
  'BALANCE_BUDGET_TO_HR' = 'balanceBudgetSentToHRReport',
  'STATUS_DUTY' = 'status_duty_report',
  'BUH_SETTINGS' = 'buh_settings',
  'EMPLOYER' = 'employer',
  'ACCOUNT_GROUPS_REPORT' = 'account_groups_report',
  'COST_CENTERS_ORG' = 'cost_centers_org',
  'AUX_VALUE' = 'aux_value',
  'DUTY_TYPE' = 'duty_type',
  'ABSENCE_CHECK' = 'absence_check',
  'AUTHORIZATION_REPORT' = 'authorisation_report',
  'TRAVEL_EXPENSE_CLAIM_REPORT' = 'travel_expense_claim'
}

export enum BALANCE_REPORTS {
  'DETAIL_OVERVIEW' = 'detail_overview',
  'BALANCE_REPORT' = 'balance_report'
}

export enum SERVICEDESK_REPORTS {
  'HOUR_BALANCE_REPORT' = 'hourbalance_report',
  'SD_EMPLOYEE' = 'sd_employee'
}

export type REPORT_TYPES =
  | BASE_REPORTS
  | ADVANCED_REPORTS
  | ADMIN_REPORTS
  | BALANCE_REPORTS
  | SERVICEDESK_REPORTS;

export type Employee = {
  empId: number;
  nameSurnameCompound: string;
  nr: number;
  nameCompound: string;
  nameCompoundSurname: string;
  nameInformal: string;
};

export type CostCenter = {
  typeName: string;
  empPool: { id: number; name: string };
  visibleUntil: string;
  code: string;
  parentBrDynId: number;
  brId: number;
  brDynId: number;
  publishDate: string;
  pool: { id: number; name: string };
  employerName: string;
  type: string;
  nameShort: string;
  valid: {
    from: string;
    to: string;
  };
  name: string;
  category: string;
  poolName: string;
};

export type Contract = {
  code: string;
  groupId: number;
  name: string;
  description: string;
  id: number;
};

export type AccountGroupData = {
  groupId: number;
  name: string;
  system: boolean;
};

export type SchemaTypes =
  | typeof baseReportSchema
  | typeof compactDutyReportSchema
  | typeof dayRosterSchema
  | typeof availabilitySchema
  | typeof requestReportSchema
  | typeof referenceDateSchema
  | typeof leaveOrArrangementReportSchema
  | typeof preferenceSchema
  | typeof authSchema
  | typeof basePlanSchema
  | object;

export type ReportDownloadingWSData = {
  type: string;
  address: string;
  body: {
    reportFileId: number;
    reportType: REPORT_TYPES;
    status: 'started' | 'failed' | 'finished';
    downloadUrl?: string;
    filename?: string;
  };
};

export type BaseType =
  | 'basic'
  | 'advanced'
  | 'admin'
  | 'balance'
  | 'servicedesk';

export enum StatusType {
  'APPROVED' = 'APPROVED',
  'PLANNED' = 'PLANNED',
  'SUBMITTED' = 'SUBMITTED'
}
